import cameraSources from './camera-sources.json';
import streamSources from './stream-sources.json';
import { StreamSource } from './types';

const spotAllowedCameraSources = cameraSources.spot.allowed;

export type SpotCameraValue = keyof typeof spotAllowedCameraSources;

type SpotCameraName = (typeof spotAllowedCameraSources)[SpotCameraValue];
export type SpotCameraSources = { name: SpotCameraName; value: SpotCameraValue }[];

export const StreamSourceNames = {
  HAND_COLOR_IMAGE: 'hand_color_image',
};

export type StreamSourceFriendly = {
  label: string;
} & StreamSource;

enum StreamSourceFilter {
  PRIMARY = 'primary',
  NCB = 'ncb',
  GRASP = 'grasp',
}

/**
 * A wrapper around dummy sources for streams.
 * Todo: Rebuild this when it comes to mapping label to value for stream sources
 */
export class CameraSources {
  static getSpotSources(): SpotCameraSources {
    return Object.entries(spotAllowedCameraSources).map(([key, value]) => ({
      name: value,
      value: key as unknown as SpotCameraValue,
    }));
  }

  // Sources without a JSON entry default to {primary: true, ncb: false,
  // grasp: false}: any source dynamically advertised via the streaming
  // topic (e.g. RTSP/SRT ingest from rtsp_whip_adapter) auto-renders in
  // the primary picker without needing a stream-sources.json edit, and
  // stays out of the SPOT-specific NCB/grasp pickers where it doesn't
  // belong. Known sources keep their explicit allowlist semantics.
  static _parseToFriendlyStreamSources(sources: StreamSource[], filter: StreamSourceFilter) {
    return sources.reduce<StreamSourceFriendly[]>((list, source) => {
      const data = streamSources[source.name as keyof typeof streamSources];
      const effective = data ?? {
        friendly: source.name,
        primary: true,
        ncb: false,
        grasp: false,
      };
      if (effective[filter]) {
        list.push({
          ...source,
          label: effective.friendly,
        });
      }
      return list;
    }, []);
  }

  static getFriendlyPrimaryStreamSources(sources: StreamSource[]) {
    return this._parseToFriendlyStreamSources(sources, StreamSourceFilter.PRIMARY);
  }

  static getFriendlyNCBStreamSources(sources: StreamSource[]) {
    return this._parseToFriendlyStreamSources(sources, StreamSourceFilter.NCB);
  }

  static getFriendlyGraspStreamSources(sources: StreamSource[]) {
    return this._parseToFriendlyStreamSources(sources, StreamSourceFilter.GRASP);
  }

  static getStreamSourcesRaw() {
    return streamSources;
  }
}
